import React from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useSearchParams } from "react-router-dom";
import Button from "components/Button/Regular";
import vgToast from "utils/toast";
import { PaymentAccount, readPaymentAccount, startPaymentOnboarding } from "services/payment-account.service";

type Props = { orgId: number };

// Target.MarketplaceFeePercent and MarketplaceFeeLimits in the API; change them together.
export const FUNELY_FEE =
  "Funely's fee is 5% of each booking, at least £2 and at most £150 (€2.50–€175, $2.50–$200). " +
  "It includes the card fees; the tools are free.";

/**
 * Getting paid through Funely. Families pay Funely once for a whole request; Funely's Stripe
 * then transfers each provider its price less Funely's fee, into the provider's own Stripe
 * account. Stripe's forms set that account up; they bring the provider back here.
 */
const PaymentAccountCard = ({ orgId }: Props) => {
  const { getAccessTokenSilently } = useAuth0();
  const [searchParams] = useSearchParams();
  const [account, setAccount] = React.useState<PaymentAccount | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [starting, setStarting] = React.useState(false);

  React.useEffect(() => {
    (async () => {
      const token = await getAccessTokenSilently();
      const { data, error: readError } = await readPaymentAccount(token, orgId);
      if (readError) setError(readError.message);
      else setAccount(data);
    })();
  }, [orgId, getAccessTokenSilently]);

  const start = async () => {
    setStarting(true);
    const token = await getAccessTokenSilently();
    const { data, error: startError } = await startPaymentOnboarding(token, orgId);
    if (startError || !data?.onboardingUrl) {
      vgToast.error(startError?.message ?? "Stripe could not start. Try again in a moment.");
      setStarting(false);
      return;
    }
    window.location.href = data.onboardingUrl;
  };

  if (error) return <p className="p-6 text-sm text-red-500">{error}</p>;
  if (!account) return <p className="p-6 text-sm text-slate-500">Loading…</p>;

  const ready = account.chargesEnabled && account.payoutsEnabled;
  const started = account.state !== "none";
  const back = searchParams.get("stripe") === "return";

  return (
    <div className="flex flex-col gap-3 p-6">
      {ready ? (
        <p className="text-sm text-slate-700">
          Your Stripe account is connected. When a family pays, your price less Funely's fee is transferred to it.
        </p>
      ) : (
        <>
          <p className="text-sm text-slate-700">
            {started
              ? "Stripe still needs some details before families can pay you through Funely."
              : "Connect a Stripe account so families can pay you through Funely. Stripe asks for your business and bank details."}
          </p>
          {back && (
            <p className="text-sm text-slate-500">
              Thanks. If you finished Stripe's forms, it can take a few minutes for Stripe to confirm them.
            </p>
          )}
          <div>
            <Button type="button" onClick={start} disabled={starting}>
              {starting ? "Opening Stripe…" : started ? "Continue with Stripe" : "Connect Stripe"}
            </Button>
          </div>
        </>
      )}
      <p className="text-xs text-slate-500">{FUNELY_FEE}</p>
    </div>
  );
};

export default PaymentAccountCard;
